// Characters that break a downstream HTML-mode renderer (Telegram alerts) or
// inject markup - stripped client-side before the request even goes out, to
// mirror the server's own belt-and-suspenders sanitizeProperties()
// (Botimza/server/routes/statsRoutes.js). The server enforces this
// regardless - this is just so a legitimate caller's data isn't silently
// mangled somewhere the caller can't see, and so nothing relies on the
// client-side pass alone as the real boundary.
const UNSAFE_CHARS_PATTERN = /[<>]/g;

function sanitizeValue(value) {
  if (typeof value === 'string') return value.replace(UNSAFE_CHARS_PATTERN, '');
  if (Array.isArray(value)) return value.map(sanitizeValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, sanitizeValue(val)]));
  }
  return value;
}

function sanitizeProperties(properties) {
  if (!properties || typeof properties !== 'object') return properties;
  return sanitizeValue(properties);
}

// Client wrapper for the in-house stats service (see
// docs/stats-mixpanel-migration-plan.md Sec6), replacing MixpanelWrapper.js.
// Mirrors its shape (track(eventName, properties)) so call-site migrations
// are near-mechanical. No identify() - resolveStatsAccountId(req) resolves
// the accountId server-side the moment a request is authenticated, so there
// is no separate anonymous-to-user linking step to perform from the client
// (see docs/stats-mixpanel-migration-plan.md Sec4).
class StatsWrapper {
  track(eventName, properties) {
    // Fire-and-forget, same convention as Mixpanel.track() today - never
    // await this at call sites, never let a failure surface to the caller.
    // credentials:'include' so the anonId/authToken cookies ride along
    // automatically; accountId is resolved server-side from them, never
    // sent by the client (see Botimza/server/routes/statsRoutes.js).
    //
    // Wrapped in try/catch, not just .catch() on the fetch promise: if
    // fetch() itself throws synchronously or doesn't return a real Promise
    // (e.g. an unconfigured test double), calling .catch() on the result
    // throws too, and analytics must never break the caller's render/request
    // either way (found via TermsAndConditionsPage.test.jsx crashing on an
    // unmocked global.fetch).
    try {
      fetch('/api/stats/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ eventName, properties: sanitizeProperties(properties) }),
      })?.catch(() => {});
    } catch {
      // Swallow - see above.
    }
  }
}

export default new StatsWrapper();
